import * as React from 'react';
import { AccentButton } from '../AccentButton';
import { t } from '../../services/HelperService';

const safeString = (v) => (v ?? '').toString();

/** The client-owned exclusive option appended to every question. */
export const INTAKE_NONE_OPTION_ID = '__none__';

/**
 * Parses an intake-questions JSON string ({ questions: [{ id, category, text,
 * allowMultiple, options: [{ id, text }] }] }) into a normalized array, or null when
 * the payload is not a question round. The server is the authoritative normalizer —
 * this only guards against a malformed round-trip.
 */
export const parseIntakeQuestions = (json) => {
  try {
    const root = JSON.parse(safeString(json) || 'null');
    if (!root || !Array.isArray(root.questions)) return null;
    const questions = root.questions
      .map((q) => ({
        id: safeString(q?.id).trim(),
        category: safeString(q?.category).trim(),
        text: safeString(q?.text).trim(),
        allowMultiple: q?.allowMultiple !== false,
        options: (Array.isArray(q?.options) ? q.options : [])
          .map((o) => ({ id: safeString(o?.id).trim(), text: safeString(o?.text).trim() }))
          .filter((o) => o.id && o.text),
      }))
      .filter((q) => q.id && q.text && q.options.length >= 2);
    return questions.length > 0 ? questions : null;
  } catch {
    return null;
  }
};

const checkboxRowStyle = (checked, disabled) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: '4px 8px',
  borderRadius: 4,
  background: checked ? '#e7f1ff' : 'transparent',
  cursor: disabled ? 'default' : 'pointer',
  fontSize: 13,
  color: '#212529',
  lineHeight: 1.4,
});

/**
 * One round of the change review's INTENT-INTAKE questions, rendered as checkbox
 * groups on the assistant's message: the user ticks the options that apply (several
 * where the question allows it) and submits — the selections become the next chat
 * turn, so the assistant reasons over them like any other answer. Every question
 * gets a client-added exclusive "None of the above"; ticking it clears the question's
 * other ticks and vice versa. Submit requires every question answered.
 *
 * Props:
 * - questions: [{ id, category, text, allowMultiple, options: [{ id, text }] }]
 * - answered?: true when this round was already submitted (frozen rendering)
 * - selections?: { [questionId]: [optionId] } — the submitted picks (for frozen rendering)
 * - disabled?: while a turn is running
 * - onSubmit(selections): called with { [questionId]: [optionId] } covering every question
 */
export const IntakeQuestionsCard = ({ questions, answered, selections, disabled, onSubmit }) => {
  const list = Array.isArray(questions) ? questions : [];
  const [picks, setPicks] = React.useState({});
  const frozen = !!answered;
  const current = frozen ? (selections ?? {}) : picks;

  const toggle = (question, optionId) => {
    if (frozen || disabled) return;
    setPicks((prev) => {
      const chosen = new Set(prev[question.id] ?? []);
      if (optionId === INTAKE_NONE_OPTION_ID) {
        // None-of-the-above is exclusive: it clears everything else.
        return { ...prev, [question.id]: chosen.has(optionId) ? [] : [INTAKE_NONE_OPTION_ID] };
      }
      chosen.delete(INTAKE_NONE_OPTION_ID);
      if (chosen.has(optionId)) {
        chosen.delete(optionId);
      } else {
        if (!question.allowMultiple) chosen.clear();
        chosen.add(optionId);
      }
      return { ...prev, [question.id]: [...chosen] };
    });
  };

  const allAnswered = list.every((q) => (current[q.id] ?? []).length > 0);

  if (list.length === 0) return null;

  return (
    <div
      data-tagname="intakeQuestionsCard"
      style={{
        border: '1px solid #cfe2ff',
        borderRadius: 8,
        background: '#f8fbff',
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        maxWidth: 640,
      }}
    >
      {list.map((question) => {
        const chosen = new Set(current[question.id] ?? []);
        return (
          <div key={question.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#212529' }}>
              {question.category && (
                <span style={{
                  display: 'inline-block',
                  marginRight: 8,
                  padding: '0px 8px',
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 600,
                  background: '#e7f1ff',
                  color: '#0a58ca',
                  whiteSpace: 'nowrap',
                }}
                >
                  {question.category}
                </span>
              )}
              {question.text}
              {question.allowMultiple && !frozen && (
                <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 400, color: '#6c757d' }}>
                  {t('(tick all that apply)')}
                </span>
              )}
            </div>
            {[...question.options, { id: INTAKE_NONE_OPTION_ID, text: t('None of the above') }].map((option) => {
              const checked = chosen.has(option.id);
              // Frozen cards only show what was picked plus muted unpicked rows.
              return (
                <label key={option.id} style={checkboxRowStyle(checked, frozen || disabled)}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={frozen || !!disabled}
                    onChange={() => toggle(question, option.id)}
                    style={{ marginTop: 2 }}
                  />
                  <span style={{ opacity: frozen && !checked ? 0.55 : 1, fontStyle: option.id === INTAKE_NONE_OPTION_ID ? 'italic' : 'normal' }}>
                    {option.text}
                  </span>
                </label>
              );
            })}
          </div>
        );
      })}

      {!frozen && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <AccentButton
            tagName="intakeSubmit"
            disabled={!!disabled || !allAnswered}
            onClick={(e) => {
              onSubmit?.({ ...picks });
              e?.promise?.();
            }}
            tooltip={allAnswered
              ? t('Send these answers to the assistant.')
              : t('Answer every question (or tick None of the above) to continue.')}
          >
            {t('Submit answers')}
          </AccentButton>
          {!allAnswered && (
            <span style={{ fontSize: 12, color: '#6c757d' }}>
              {t('Answer every question to continue — "None of the above" counts.')}
            </span>
          )}
        </div>
      )}
      {frozen && (
        <div style={{ fontSize: 12, color: '#6c757d', fontStyle: 'italic' }}>
          {t('Answers sent.')}
        </div>
      )}
    </div>
  );
};
